/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { App, Tooltip } from 'antd'
import { formatPhoneNumber } from 'react-phone-number-input'
import { MailOutlined, PhoneOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightMedium,
  weightRegular,
  paragraphLarge,
  paragraphXSmall,
} from 'src/components/css'
import type { FamilyMember } from '../../hooks/useFamilyMembers'
import StatusTag from 'src/components/ts-components/StatusTag'

interface Props {
  member: FamilyMember
}

const FamilyMemberRow: React.FC<Props> = ({ member }) => {
  const { message } = App.useApp()

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      message.success(`${label} copied to clipboard`)
    } catch {
      message.error(`Couldn't copy ${label.toLowerCase()}`)
    }
  }

  // Second row reads: relation · email · phone (phone only when present),
  // joined by muted separator dots.
  const detailParts: React.ReactNode[] = []
  if (member.relationshipLabel) {
    detailParts.push(<span key='relation'>{member.relationshipLabel}</span>)
  }
  if (member.email) {
    detailParts.push(<span key='email'>{member.email}</span>)
  }
  if (member.phone) {
    // Match the app's other list/detail views — display the number in national
    // format, e.g. "(604) 353-8877". Fall back to the raw value if it can't be
    // parsed.
    detailParts.push(
      <span key='phone'>{formatPhoneNumber(member.phone) || member.phone}</span>
    )
  }

  return (
    <div css={rowStyle}>
      <div css={infoColStyle}>
        <span css={nameRowStyle}>
          <span css={nameStyle}>{member.name}</span>
          {member.status ? (
            <StatusTag
              tone={member.isAccepted ? 'success' : 'warning'}
              label={member.status}
              dot
            />
          ) : null}
          {member.isPrimary ? <StatusTag tone='info' label='Admin' /> : null}
        </span>
        {detailParts.length > 0 ? (
          <span css={detailStyle}>
            {detailParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 ? (
                  <span
                    css={[dotStyle, separatorDotStyle]}
                    aria-hidden='true'
                  />
                ) : null}
                {part}
              </React.Fragment>
            ))}
          </span>
        ) : null}
      </div>

      <div css={iconsRowStyle}>
        {/* Phone + email copy buttons only show once the invite is accepted;
            an invited/pending contact has no usable contact actions yet. Order
            left→right: phone, email. Tapping copies it. */}
        {member.isAccepted && member.phone ? (
          <Tooltip
            title={capitalizeWords('Copy Phone Number')}
            /* The modal wrapper sits at zIndex 10001; the tooltip portals out,
               so without a higher zIndex it renders behind the modal. */
            zIndex={10050}
          >
            <button
              type='button'
              css={iconButtonStyle}
              onClick={() =>
                copyToClipboard(member.phone as string, 'Phone number')
              }
              aria-label={`Copy ${member.name}'s phone number`}
            >
              <PhoneOutlined css={phoneIconStyle} />
            </button>
          </Tooltip>
        ) : null}

        {member.isAccepted && member.email ? (
          <Tooltip title={capitalizeWords('Copy Email')} zIndex={10050}>
            <button
              type='button'
              css={iconButtonStyle}
              onClick={() => copyToClipboard(member.email as string, 'Email')}
              aria-label={`Copy ${member.name}'s email`}
            >
              <MailOutlined css={mailIconActiveStyle} />
            </button>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

export default FamilyMemberRow

// ─── Styles ────────────────────────────────────────────────────────────────

const rowStyle = css`
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const infoColStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

// Name + status chip sit on the first line.
const nameRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const nameStyle = css`
  ${paragraphLarge}
  ${weightMedium}
  ${font.body}
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Single-line detail: relation · email · phone. Never wraps — the line stays
// on one row and truncates the overflowing tail with an ellipsis so the row
// height is stable. Rendered as inline text (not flex) so the whole line is one
// truncatable unit; the separator dots are inline-block so they flow with it.
const detailStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  ${font.body}
  font-size: 14px;
  color: ${colors.NEUTRAL_50};
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Separator dots between relation · email · phone. Inline-block so they sit on
// the single detail line and don't introduce a flex break.
const dotStyle = css`
  display: inline-block;
  vertical-align: middle;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  margin: 0 6px;
`

const separatorDotStyle = css`
  background-color: ${colors.NEUTRAL_100};
`

const iconsRowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`

// Icon-only copy button — bare reset so it reads as the icon, not a button.
const iconButtonStyle = css`
  line-height: 0;
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`

const mailIconActiveStyle = css`
  font-size: 24px;
  color: ${colors.PRIMARY};
`

const phoneIconStyle = css`
  font-size: 24px;
  color: ${colors.PRIMARY};
`
